import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CANONICAL_BOOKS,
  CANONICAL_SLUG_TO_NAME,
  ensureBibleHydrated,
  getBsbMeta,
  countBsbBooks,
  parseReference,
  searchVerses,
  testamentOf,
} from '@/lib/bible/active';
import type {
  CanonicalBookEntry,
  ParsedReference,
  SearchResult,
  VerseMatchSpan,
} from '@/lib/bible/active';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useNoticeStackClearance, useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { bibleErrorFields, reportBibleEvent } from '@/lib/bible-telemetry';
import BibleBootstrapIndicator from '@/components/BibleBootstrapIndicator';
import { useBibleBootstrapStore } from '@/lib/bsb-progress-store';
import { ContinueReadingCard } from '@/components/ContinueReadingCard';
import {
  BIBLE_BOOKMARKS_ENABLED,
  BIBLE_OVERVIEW_ENABLED,
  BIBLE_READER_V2_ENABLED,
  PRAY_MODE_ENABLED,
  SCRIPTURE_JOURNAL_ENABLED,
} from '@/lib/feature-flags';
import { isOverviewLocaleSupported } from '@/lib/bible-overview/search';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { useHighlightStore } from '@/lib/highlight-store';
import { HL_DOT_HEX, HL_FAMILY_KEYS, coerceFamily } from '@/lib/highlight-utils';
import { useAdiaStore } from '@/lib/store';
import {
  loadTopics,
  resolveTopicVerseText,
  searchTopicsWithStrength,
  type BundledTopic,
  type BundledTopicVerse,
  type TopicMatchStrength,
} from '@/lib/bible-search/topics';
import { orderTopSections, type TopSection } from '@/lib/bible-search/top-order';
import {
  loadStories,
  searchStories,
  searchStoriesByName,
  storiesForTopic,
  storyId,
  type BundledStory,
} from '@/lib/bible-search/stories';
import { searchBooks } from '@/lib/bible-search/book-search';
import {
  readBibleIndexSnapshot,
  writeBibleIndexSnapshot,
  clearBibleIndexSnapshot,
  type BibleIndexSnapshot,
} from '@/lib/bible-index-view-store';
import { getReadingAccessSnapshot, useReadingAccess, useDefinitelyOffline } from '@/lib/reading-access';
import type { BibleHighlight } from '@/types/highlight';
import styles from './BibleBookIndex.module.css';

const EMPTY_HIGHLIGHTS: BibleHighlight[] = [];

/**
 * Bible book index at /bible (M.3.8.a, banked principle
 * P-M.3.8-Canonical-Order). Renders all 66 books in canonical order,
 * partitioned OT (1..39) and NT (40..66). Tapping a book expands a
 * chapter grid; each chapter cell is a router Link to
 * /bible/{slug}/{n}.
 *
 * Hydration: BSB books live in IDB after a successful
 * ensureBibleHydrated() run. Book list AND chapter grids both render
 * synchronously from CANONICAL_BOOKS (source, not IDB): the chapter
 * count is the static CANONICAL_BOOKS[].chapters, so an expand does
 * ZERO I/O and the first tap on any book is instant online and offline
 * (offline-first Chunk 3 §4.C). The chapter grid is gated on the
 * index-level hydrationState (a chapter tap navigates into the reader,
 * which needs BSB in IDB), not on a per-book IDB read.
 *
 * Per-book "loading"/"failed" signals are carried by the index-level
 * hydrationState (no per-book fetch). The page-level "Bible is
 * initializing" indicator is still deliberately omitted per M.3.8.a
 * Phase 2.5 (option h): no global mount survived the collision matrix
 * on small viewports.
 *
 * All Bible-data imports route through @/lib/bible/active (banked
 * principle P-M.3.8-Translation-Facade). Do not import from bsb-*
 * directly.
 */

// Bounded result list (roadmap decision 5): no virtualization, cap at
// the engine default and surface a "refine" hint when capped.
const VERSE_RESULT_LIMIT = 50;

// Topic results (Layer 3): cap the matched-topic list, and show a few
// curated verses per topic with an expand affordance for the rest (rule
// #16 bounded lists, no virtualization).
const TOPIC_RESULT_LIMIT = 4;
const TOPIC_VERSE_PREVIEW = 3;

// Story results (Layer 4): a small, bounded set (rule #16, no
// virtualization). Three-tier merge (08.3 piece 2): explicit story-name /
// common-name alias matches lead, then topic-driven stories, then generic
// direct (essence/procession) matches, deduped by storyId.
const STORY_RESULT_LIMIT = 6;

// Book-name results (arc 07.2): a bare book-name query surfaces matching books
// as the SAME accordion + chapter grid as the main index. Bounded, no
// virtualization. searchBooks is synchronous (pure over CANONICAL_BOOKS), so it
// is derived in a useMemo, not an async stamped effect.
const BOOK_RESULT_LIMIT = 8;

// Result-type filter tabs (arc 07.3). A 'Top' grouped preview leads with the
// best of each present category; per-type chips filter to one category. A chip
// only renders for a category that has results; 'Top' only when >= 2 categories
// are present. Chip order is fixed (Top, Verses, Books, Topics, Stories); the
// Top preview's section order is intent-RANKED by orderTopSections (08.4):
// Books first, Stories last, Verses↔Topics swapped by match strength.
type SearchTab = 'top' | 'verses' | 'books' | 'topics' | 'stories';
const TAB_LABEL_KEY: Record<SearchTab, string> = {
  top: 'bibleSearch.tabTop',
  verses: 'bibleSearch.tabVerses',
  books: 'bibleSearch.tabBooks',
  topics: 'bibleSearch.tabTopics',
  stories: 'bibleSearch.tabStories',
};
// Per-category caps in the Top grouped preview (locked).
const TOP_BOOK_CAP = 4;
const TOP_VERSE_CAP = 3;
const TOP_TOPIC_CAP = 2;
const TOP_STORY_CAP = 2;

// Topic match-strength rank (08.4): lower = stronger, for picking the strongest
// band among the live topic matches to feed orderTopSections.
const TOPIC_STRENGTH_RANK: Record<TopicMatchStrength, number> = {
  whole: 0,
  direct: 1,
  synonym: 2,
  partial: 3,
  fuzzy: 4,
  none: 5,
};

// Merge ordered story lists, dedup by storyId, cap at limit. The lane is
// merged direct-NAME-first: explicit story-name / common-name alias matches
// (searchStoriesByName) lead, THEN topic-driven entries (the topic context is
// why they surfaced), THEN generic direct name/essence matches (searchStories).
// An explicit name/alias match is a stronger intent than an incidental topic
// or essence/procession hit and must never be buried past the cap (08.3 piece
// 2, the gold-set-caught fix). A purely topical query has an empty name list,
// so topic-driven still leads and broad topical story results are unchanged.
function mergeStories(lists: BundledStory[][], limit: number): BundledStory[] {
  const seen = new Set<string>();
  const out: BundledStory[] = [];
  for (const list of lists) {
    for (const s of list) {
      const id = storyId(s);
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(s);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

// Verse-reference register: CANONICAL_SLUG_TO_NAME returns `name`
// ("Psalm" singular), the form used for verse citations. Falls back to
// the slug only if the map is somehow missing the book.
function referenceLabel(ref: ParsedReference): string {
  const name = CANONICAL_SLUG_TO_NAME.get(ref.bookSlug) ?? ref.bookSlug;
  return ref.verseStart != null
    ? `${name} ${ref.chapter}:${ref.verseStart}`
    : `${name} ${ref.chapter}`;
}

// Jump target: chapter-only refs omit ?v; verse refs carry the start
// verse (the verse-jump branch in BibleChapter scrolls to it).
function referenceTo(ref: ParsedReference): string {
  const base = `/bible/${ref.bookSlug}/${ref.chapter}`;
  return ref.verseStart != null ? `${base}?v=${ref.verseStart}` : base;
}

// Slice a snippet into plain + <mark> segments using the engine-supplied
// spans (sorted, non-overlapping offsets INTO the snippet, chunk 02
// contract). Pure string slicing, no re-search at render time.
function highlightSnippet(
  snippet: string,
  spans: VerseMatchSpan[],
  markClass: string,
): ReactNode {
  if (spans.length === 0) return snippet;
  const parts: ReactNode[] = [];
  let cursor = 0;
  spans.forEach((span, i) => {
    if (span.start > cursor) parts.push(snippet.slice(cursor, span.start));
    parts.push(
      <mark key={`m-${i}`} className={markClass}>
        {snippet.slice(span.start, span.end)}
      </mark>,
    );
    cursor = span.end;
  });
  if (cursor < snippet.length) parts.push(snippet.slice(cursor));
  return parts;
}

type HydrationState = 'pending' | 'ready' | 'failed';

export default function BibleBookIndex() {
  const { t, i18n } = useTranslation();
  const reading = useReadingAccess();
  const definitelyOffline = useDefinitelyOffline();
  // Bible overview entry (the map of Scripture): flag-gated AND locale-gated
  // (notes are en-US only, rule #69) so an es/pt reader never sees a row that
  // leads to an empty catalog.
  const showOverviewLink =
    BIBLE_OVERVIEW_ENABLED && isOverviewLocaleSupported(i18n.language);
  // Scripture Journal entry-button count (Phase 4 §7). Live record count;
  // tombstones already excluded by the store. Under v2 the journal also holds
  // highlights (Chunk C §6), so count the MERGED rows; v2-off never loads the
  // highlight store AND the `? : 0` gate forces 0, so the count is byte-identical.
  const liveBookmarkCount = useBookmarkStore((s) => s.bookmarks.length);
  const bookmarkCount = reading.restricted ? (reading.local?.bookmarks.length ?? 0) : liveBookmarkCount;
  const liveHighlightCount = useHighlightStore((s) => s.highlights.length);
  const highlightCount = reading.restricted ? (reading.local?.highlights.length ?? 0) : liveHighlightCount;
  // Scripture-anchored prayers also populate the journal (pray-in-journal §3.2);
  // Every term is independently flag-gated so a disabled source contributes
  // neither rows nor a stale in-memory count.
  const livePrayerJournalCount = useAdiaStore(
    (s) => s.prayers.filter((p) => p.scriptureAnchor != null).length,
  );
  const prayerJournalCount = reading.restricted
    ? (reading.local?.prayers.filter((p) => p.scriptureAnchor != null).length ?? 0)
    : livePrayerJournalCount;
  const journalCount =
    (BIBLE_BOOKMARKS_ENABLED ? bookmarkCount : 0) +
    (BIBLE_READER_V2_ENABLED ? highlightCount : 0) +
    (PRAY_MODE_ENABLED ? prayerJournalCount : 0);
  // Journal tile colour band: the reader's own highlight meanings, in palette
  // order, each segment sized by its count. Empty (and so not rendered) when
  // there are no highlights, so it can never read as an empty progress bar.
  const liveHighlights = useHighlightStore((s) => s.highlights);
  const highlights = reading.restricted ? (reading.local?.highlights ?? EMPTY_HIGHLIGHTS) : liveHighlights;
  const journalBand = useMemo(() => {
    if (!BIBLE_READER_V2_ENABLED) return [];
    const counts = new Map<string, number>();
    for (const h of highlights) {
      const family = coerceFamily(h.family);
      if (family) counts.set(family, (counts.get(family) ?? 0) + 1);
    }
    return HL_FAMILY_KEYS.filter((family) => counts.has(family)).map((family) => ({
      family,
      count: counts.get(family) ?? 0,
    }));
  }, [highlights]);

  // Restoration snapshot (navigation chunk C, README §6.1/§6.3). Read ONCE at
  // mount via a pure read (NO clear in render, safe against a discarded
  // concurrent/StrictMode render); the mount effect below CONSUMES (clears) it.
  // The expandedSlugs/query initializers below seed from this captured object.
  const restoreRef = useRef<BibleIndexSnapshot | null | undefined>(undefined);
  // App notice stack: the in-flow spacer below the Back link keeps the title,
  // the offline status, the search row and the books below the fixed notices.
  // While a notice shows, the sticky section heading and the expanded book row
  // stick below the notices too (`--notice-clearance` on the container offsets
  // their CSS `top`; unset, both stay at their former 0 and 52 px), and the
  // browse-restore anchor lands the restored row below them.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  const noticeClearance = useNoticeStackClearance();
  const noticeClearanceRef = useRef(noticeClearance);
  useEffect(() => {
    noticeClearanceRef.current = noticeClearance;
  }, [noticeClearance]);
  if (restoreRef.current === undefined) restoreRef.current = readBibleIndexSnapshot();
  const restore = restoreRef.current; // BibleIndexSnapshot | null, stable this mount

  // The continue-reading card reads this device's storage after mount and
  // then appears above the tiles and the book list. Until its first read
  // settles (a few ms; 300 ms at most), the tiles and books stay hidden
  // (visibility only: layout is unchanged), so they appear once, in place,
  // instead of jumping down under a reader's eyes. The browse restore below
  // waits for the same moment, so the card cannot push the restored row.
  const [entrySettled, setEntrySettled] = useState(false);
  const markEntrySettled = useCallback(() => setEntrySettled(true), []);
  useEffect(() => {
    if (entrySettled) return;
    const timer = setTimeout(markEntrySettled, 300);
    return () => clearTimeout(timer);
  }, [entrySettled, markEntrySettled]);

  const [hydrationState, setHydrationState] = useState<HydrationState>('pending');
  // Offline-aware messaging (§4.1): a failed hydration is "offline" when BSB was
  // never downloaded and there is no network. Drives the honest copy.
  const [hydrationOffline, setHydrationOffline] = useState(false);
  // Available-offline affordance (§4.3): null while the IDB meta read is in
  // flight, then true (downloaded) / false (not yet).
  const [hasMeta, setHasMeta] = useState<boolean | null>(null);
  // Visible download moment (§4.2): the indicator self-shows while this is
  // true; we also read it here to gate the §4.3 status lines so they never
  // co-render with the "preparing..." indicator.
  const isBibleBootstrapping = useBibleBootstrapStore(
    (s) => s.isBibleBootstrapping,
  );
  // Truthful save inventory (2026-08-25): "N of 66 books" while the
  // resumable hydration runs. Seeds from the checkpoint on a resumed
  // run, so an interrupted download picks up at "18 of 66", not zero.
  const bootstrapBooksDone = useBibleBootstrapStore(
    (s) => s.bootstrapBooksDone,
  );
  const bootstrapBooksTotal = useBibleBootstrapStore(
    (s) => s.bootstrapBooksTotal,
  );
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(() =>
    restore?.intent === 'browse' ? new Set(restore.expandedSlugs) : new Set(),
  );
  // SEPARATE expansion state for the Books-search accordions (arc 07.2), so a
  // book expanded inside search results never leaks into the normal index
  // listing after the search is cleared. No per-book IDB cache: chapter grids
  // render from the static CANONICAL_BOOKS count (§4.C), so expansion is the
  // only per-book state either surface needs.
  const [searchExpandedSlugs, setSearchExpandedSlugs] = useState<Set<string>>(
    () => new Set(),
  );

  // --- Search (Bible-search Layers 1 + 2 + 3 + 4) -------------------------
  // Layer 1 reference jump, Layer 2 verse scan, Layer 3 topic results, Layer
  // 4 stories. The verse, topic, and story paths share this one debounced
  // query but load independently (see their effects below). The controlled
  // input stays
  // immediate; a 150 ms debounced copy feeds the scans so the 31k-verse
  // search does not run on every keystroke (debounce is owned by this chunk,
  // not chunk 06). Reference parsing is pure + cheap, so it runs off the
  // immediate query for an instant "Go to" banner.
  const [query, setQuery] = useState(() =>
    restore?.intent === 'search' ? restore.query : '',
  );
  const debouncedQuery = useDebouncedValue(query, 150);
  // Verse results are STAMPED with the query they were computed for; the
  // render gate only shows a result whose stamp matches the live debounced
  // query, so a stale frame never paints (mirrors Garden's stamped count,
  // rule #38). The empty branch returns without a synchronous setState (the
  // stamp simply stops matching), so no set-state-in-effect deviation is
  // needed here.
  const [verseSearch, setVerseSearch] = useState<{
    query: string;
    result: SearchResult;
  } | null>(null);
  // Topic results (Layer 3) are an INDEPENDENT load path from the verse
  // scan (a topics-bundle failure never blocks verse search, mirroring the
  // picker's decoupled-paths posture, rule #58). Stamped with its query
  // like the verse result so a stale resolve never paints.
  const [topicSearch, setTopicSearch] = useState<{
    query: string;
    topics: Array<{ topic: BundledTopic; strength: TopicMatchStrength }>;
  } | null>(null);
  // Per-topic expanded set: a topic shows TOPIC_VERSE_PREVIEW verses until
  // the user taps "Show all N verses". Reset whenever a new match set lands.
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(
    () => new Set(),
  );
  // Story results (Layer 4) merge three paths name-first: explicit name/alias
  // (searchStoriesByName), then topic-driven (storiesForTopic per matched
  // topic), then generic direct name/essence (searchStories) — deduped +
  // capped. Stamped with its query so a stale resolve never paints.
  const [storySearch, setStorySearch] = useState<{
    query: string;
    stories: BundledStory[];
  } | null>(null);
  // Per-story prose expansion: prose is clamped until the user taps "Read
  // more". Reset whenever a new match set lands.
  const [expandedStories, setExpandedStories] = useState<Set<string>>(
    () => new Set(),
  );
  // Active result-type tab (arc 07.3). A NEW search resets to 'top'; the
  // derived effectiveTab (below) is what actually renders, so an async
  // category settle never strands the user on a now-empty tab.
  const [activeTab, setActiveTab] = useState<SearchTab>('top');

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery !== '';
  const parsedRef = useMemo(
    () => (trimmedQuery ? parseReference(trimmedQuery) : null),
    [trimmedQuery],
  );

  // Component-scope mount guard for click-handler-invoked async
  // (mirrors Picker.tsx mountedRef pattern). Effects that own their
  // own cancelled-flag are not gated on this.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // --- Restoration (navigation chunk C, README §6.2/§6.3) ------------------
  // Per-book accordion <li> DOM nodes (callback ref map, keyed by slug) for the
  // browse-anchor scroll; the in-flight settle rAF; the pending browse-anchor
  // slug; and the one-shot select-all arm for a restored search query.
  const bookRowRefs = useRef<Map<string, HTMLLIElement | null>>(new Map());
  const settleRafRef = useRef<number | null>(null);
  const pendingBrowseScrollRef = useRef<string | null>(null);
  const pendingSelectAllRef = useRef(false);
  // Scroll-anchor for the single-open toggle: hold the tapped book's header
  // visually still when the auto-closing book above it collapses (see the
  // toggleBook + useLayoutEffect pair below).
  const toggleAnchorRef = useRef<{ slug: string; top: number } | null>(null);

  // slug → canonical order (document order == OT 1–39 then NT 40–66), built once.
  const slugOrder = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of CANONICAL_BOOKS) m.set(b.slug, b.order);
    return m;
  }, []);

  // Outbound-intent writers (README §6.2). Each runs in a Link onClick BEFORE
  // navigation, stamping the snapshot the index restores from on return.
  const writeBrowseSnapshot = useCallback(
    (activeBookSlug: string) => {
      writeBibleIndexSnapshot({
        expandedSlugs: [...expandedSlugs],
        activeBookSlug,
        query: '', // browse implies no active query (accordions hidden while searching)
        intent: 'browse',
      });
    },
    [expandedSlugs],
  );
  // Parameterized so each link records the query that produced its own content
  // (README §6.2): result links pass debouncedQuery.trim() (the stamp their
  // visible verses are gated on); the reference banner passes trimmedQuery.
  const writeSearchSnapshot = useCallback((q: string) => {
    writeBibleIndexSnapshot({
      expandedSlugs: [],
      activeBookSlug: null,
      query: q,
      intent: 'search',
    });
  }, []);
  const writeNeutralSnapshot = useCallback(() => {
    writeBibleIndexSnapshot({
      expandedSlugs: [],
      activeBookSlug: null,
      query: '',
      intent: 'neutral',
    });
  }, []);

  // Mount: CONSUME the snapshot (single-use, §6.1) and dispatch the scroll
  // intent. browse → defer to the settle effect (waits for grids to load);
  // search → top + arm one-shot select-all (decision #1); neutral/none → top.
  useEffect(() => {
    const snap = restore;
    clearBibleIndexSnapshot();
    if (!snap || snap.intent === 'neutral') {
      // Clean top. Normalize scroll explicitly: there is no ScrollRestoration,
      // so browser/swipe back can otherwise restore the prior /bible scroll and
      // fail the Continue Reading + clean-index gates.
      window.scrollTo(0, 0);
      return;
    }
    if (snap.intent === 'search') {
      pendingSelectAllRef.current = true;
      window.scrollTo(0, 0);
      return;
    }
    // browse
    if (snap.activeBookSlug) {
      pendingBrowseScrollRef.current = snap.activeBookSlug;
    } else {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browse-anchor scroll (README §6.3, Blocker 5). Re-runs on hydrationState so
  // it fires once the grids can render. With static-count grids (§4.C), every
  // restored expanded book draws its grid SYNCHRONOUSLY once hydrationState ===
  // 'ready' (no per-book IDB load to wait on), so the height of every row at or
  // above the anchor is final at that point and a late row can't push the
  // anchor down. The bounded-rAF loop below then confirms layout stability. The
  // pending ref is cleared only AFTER scrollTo fires, so a settle pass canceled
  // by a mid-window dep change restarts instead of dropping the scroll.
  useEffect(() => {
    const slug = pendingBrowseScrollRef.current;
    if (!slug || !restore) return;
    const activeOrder = slugOrder.get(slug);
    if (activeOrder == null) {
      pendingBrowseScrollRef.current = null;
      return;
    }
    if (hydrationState !== 'ready') return; // stay PENDING until grids can render
    // The continue-reading card above can still push the anchor down; wait for
    // its first read (the list is hidden until then anyway).
    if (!entrySettled) return;
    const row = bookRowRefs.current.get(slug);
    if (!row) return;
    // Sticky section heading (≈52 px, .sectionHeading top:0) + a small breath,
    // below the app notice stack while a notice shows (0 otherwise).
    const STICKY_GAP = 60;
    let frames = 0;
    let stable = 0;
    let lastTop = Number.NaN;
    const tick = () => {
      const top = row.getBoundingClientRect().top + window.scrollY;
      if (top === lastTop) stable += 1;
      else {
        stable = 0;
        lastTop = top;
      }
      frames += 1;
      if (stable >= 2 || frames >= 10) {
        window.scrollTo(0, Math.max(0, top - STICKY_GAP - noticeClearanceRef.current));
        pendingBrowseScrollRef.current = null; // one-shot: cleared only after the scroll fires
        settleRafRef.current = null;
        return;
      }
      settleRafRef.current = requestAnimationFrame(tick);
    };
    settleRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (settleRafRef.current) cancelAnimationFrame(settleRafRef.current);
    };
  }, [hydrationState, restore, slugOrder, entrySettled]);

  // Hydration kick. startHydration flips state to 'pending' FIRST so
  // the UI visually transitions out of any prior 'failed' state on
  // tap-retry, even if the underlying ensureBibleHydrated resolves
  // quickly. Cancellation guard prevents setState after unmount.
  const hydrationStartedRestricted = useRef(false);
  const [admissionRetry, setAdmissionRetry] = useState(0);
  const startHydration = useCallback(() => {
    hydrationStartedRestricted.current = getReadingAccessSnapshot().restricted;
    setHydrationState('pending');
    setHydrationOffline(false);
    let cancelled = false;
    ensureBibleHydrated()
      .then(() => {
        if (cancelled || !mountedRef.current) return;
        setHydrationState('ready');
        setHasMeta(true); // a successful hydrate means BSB is in IDB (§4.3)
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        console.warn('[bible-index] hydration failed:', err);
        const definitelyOffline =
          typeof navigator !== 'undefined' && !navigator.onLine;
        // Offline failures are expected; apparent-online hydration
        // failures are the reliability signal.
        if (!definitelyOffline && !getReadingAccessSnapshot().restricted) {
          reportBibleEvent('bible_hydration_failed', bibleErrorFields(err));
        }
        setHydrationState('failed');
        // "Offline" is claimed ONLY when the device is definitely offline
        // (navigator.onLine === false). The pre-2026-08-25 shape also
        // treated manifest_unavailable_no_cache as offline, which told
        // CONNECTED users to "connect once" after any transient request
        // failure on a fresh device. An apparently-online failure gets
        // the honest "couldn't download, tap to retry" copy instead.
        setHydrationOffline(definitelyOffline);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Read the BSB IDB meta once on mount for the available-offline affordance
  // (§4.3). A successful hydrate also flips hasMeta true above; this covers
  // the already-hydrated case where ensureBibleHydrated resolves as a no-op.
  // VERIFIED claim (2026-08-25): "Available offline." requires the meta row
  // AND the cheap count witness agreeing all 66 books are actually present —
  // a meta row over a partial store must not claim readiness (the resumable
  // hydrate repairs the gap on its next online run).
  useEffect(() => {
    let cancelled = false;
    Promise.all([getBsbMeta(), countBsbBooks()])
      .then(([meta, count]) => {
        if (!cancelled && mountedRef.current) {
          setHasMeta(!!meta && count >= CANONICAL_BOOKS.length);
        }
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) setHasMeta(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cancel = startHydration();
    return cancel;
  }, [startHydration, admissionRetry]);

  useEffect(() => {
    if (reading.restricted || hydrationState !== 'failed' || !hydrationStartedRestricted.current) return;
    hydrationStartedRestricted.current = false;
    // The initial cache-only miss can download after verification succeeds.
    setAdmissionRetry((attempt) => attempt + 1);
  }, [reading.restricted, hydrationState]);

  // Offline availability of the Layer 3/4 search bundles (Bible-search Plan
  // 06). TWO deliberately decoupled mechanisms:
  //
  // 1. SW precache (offline, survives close + update deploys). We ask the
  //    service worker to write the bundles into ITS current CACHE_NAME, because
  //    only the SW knows the per-deploy bumped cache name and only an SW-context
  //    write is deterministic. A page fetch is cached only when the SW controls
  //    the page (misses a fresh first visit), caches into the OLD cache name on
  //    an update deploy (deleted on the new SW's activate), and is suppressed by
  //    the loaders' module promise cache. So instead we send a bare trigger to
  //    the active worker after `ready`, and again on `controllerchange` so a NEW
  //    worker taking over on an update caches into the new CACHE_NAME. The SW
  //    owns a hardcoded URL allowlist; we send no URLs, no data, no UI. No
  //    timeout marks the cache warm: only an SW acknowledgement-by-action does.
  // 2. In-memory loader warm (this session only). loadTopics()/loadStories()
  //    populate the module promise cache so the first search this session is
  //    instant. Kept SEPARATE from the SW precache so the promise cache cannot
  //    suppress the SW's own cache write.
  //
  // Cost rides the only surface with a search box, which already triggers the
  // BSB hydrate (rule #66: only Bible-surface users pay; never on app boot).
  useEffect(() => {
    if (definitelyOffline || reading.restricted || getReadingAccessSnapshot().restricted) return;
    void loadTopics().catch(() => {});
    void loadStories().catch(() => {});

    if (!('serviceWorker' in navigator)) return;
    const trigger = (sw: ServiceWorker | null) => {
      if (!navigator.onLine || getReadingAccessSnapshot().restricted) return;
      sw?.postMessage({ type: 'precache-bible-search' });
    };
    navigator.serviceWorker.ready
      .then((reg) => trigger(reg.active))
      .catch(() => {});
    const onControllerChange = () => trigger(navigator.serviceWorker.controller);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
    };
  }, [reading.restricted, definitelyOffline]);

  useEffect(() => {
    const prior = document.title;
    document.title = t('bible.title');
    return () => {
      document.title = prior;
    };
  }, [t]);

  // Verse search (Layer 2), debounced. Waits for ensureBibleHydrated
  // (single-flight, cheap if already hydrated) before searchVerses, which
  // reads the lazy in-memory book cache. The result is stamped with its
  // query so the render gate (liveResult below) discards a stale resolve.
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q === '') return; // stamp simply stops matching; no setState here
    let cancelled = false;
    // A partially cached Bible is still searchable without a manifest fetch.
    const ready = prepareCachedSearch();
    ready.then(() => searchVerses(q, { limit: VERSE_RESULT_LIMIT }))
      .then((result) => {
        if (cancelled || !mountedRef.current) return;
        setVerseSearch({ query: q, result });
        // DEV-only related-wording metrics (08.5): never runs in the production
        // bundle real users get, so no search term ever leaves the device (rule
        // 07.4). attempted = denominator, succeeded (topMatchKind 'related') =
        // numerator, attempted-vs-matched = per-group success/fail.
        if (import.meta.env.DEV && result.relatedAttemptedTerms?.length) {
          console.debug('[bible-search][related]', {
            succeeded: result.topMatchKind === 'related',
            attempted: result.relatedAttemptedTerms,
            matched: result.relatedTerms,
            hits: result.hits.length,
            query: q,
          });
        }
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        console.warn('[bible-search] verse search failed:', err);
        setVerseSearch({
          query: q,
          result: { hits: [], fuzzy: false, capped: false, topMatchKind: null, relatedTerms: [] },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, reading.restricted]);

  // Topic search (Layer 3), debounced on the same query. Lazy-loads the
  // offline topics bundle (module-cached after first load, SW-cached
  // thereafter) and matches via the shared topic matcher. Independent of
  // the verse scan: a bundle failure degrades to "no topics" without
  // touching verse results. Stamped with the query so a stale resolve is
  // discarded by the liveTopics gate. Resets the per-topic expansion set
  // each time a fresh match set lands.
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q === '') return; // stamp simply stops matching; no setState here
    let cancelled = false;
    loadTopics({ localOnly: reading.restricted || getReadingAccessSnapshot().restricted || !navigator.onLine })
      .then((topics) => searchTopicsWithStrength(q, topics).slice(0, TOPIC_RESULT_LIMIT))
      .then((matched) => {
        if (cancelled || !mountedRef.current) return;
        setTopicSearch({ query: q, topics: matched });
        setExpandedTopics(new Set());
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        console.warn('[bible-search] topic search failed:', err);
        setTopicSearch({ query: q, topics: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, reading.restricted]);

  // Story search (Layer 4), debounced on the same query. Lazy-loads the
  // offline spine bundle (module-cached after first load, SW-cached
  // thereafter) and merges two paths: topic-driven (storiesForTopic for each
  // matched topic, read off the topicSearch stamp) and direct name/essence
  // (searchStories over the whole bundle). topicSearch is a dep so once the
  // topics settle the topic-driven stories fold in; until then a direct-only
  // set renders. Independent of the verse + topic LOADS (a spine-bundle
  // failure degrades to "no stories", rule #58 decoupled paths). Stamped
  // with the query so a stale resolve is discarded by the liveStories gate.
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q === '') return; // stamp simply stops matching; no setState here
    let cancelled = false;
    const matchedSlugs =
      topicSearch && topicSearch.query === q
        ? topicSearch.topics.map((tp) => tp.topic.slug)
        : [];
    loadStories({ localOnly: reading.restricted || getReadingAccessSnapshot().restricted || !navigator.onLine })
      .then((bundle) => {
        const byName = searchStoriesByName(q, bundle.stories);
        const topicDriven = matchedSlugs.flatMap((slug) =>
          storiesForTopic(slug, bundle),
        );
        const direct = searchStories(q, bundle.stories);
        const merged = mergeStories(
          [byName, topicDriven, direct],
          STORY_RESULT_LIMIT,
        );
        if (cancelled || !mountedRef.current) return;
        setStorySearch({ query: q, stories: merged });
        setExpandedStories(new Set());
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        console.warn('[bible-search] story search failed:', err);
        setStorySearch({ query: q, stories: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, topicSearch, reading.restricted]);

  // A NEW search starts on the Top tab (arc 07.3). This is belt-and-suspenders
  // with the derived effectiveTab: the reset gives a fresh query a Top default,
  // and effectiveTab handles async category settle so a selected tab that
  // becomes empty mid-settle never strands the view.
  useEffect(() => {
    setActiveTab('top');
  }, [debouncedQuery]);

  // Expansion toggle. Chapter grids render from the static CANONICAL_BOOKS
  // count (§4.C), so an expand does NO per-book fetch; it only flips the
  // expansion set, and the grid draws synchronously when hydrationState is
  // 'ready'. (Pre-§4.C this fired a lazy getBook + a pending->ready transition
  // effect; both are gone with the per-book IDB read.)
  const toggleBook = (slug: string) => {
    // Capture the tapped row's viewport position BEFORE the state change so the
    // useLayoutEffect below can hold it steady. Single-open collapses the
    // previously-open book; when that book sits ABOVE the tapped one, the
    // collapse would otherwise yank the tapped header upward under the finger.
    const row = bookRowRefs.current.get(slug);
    toggleAnchorRef.current = row
      ? { slug, top: row.getBoundingClientRect().top }
      : null;
    // Single-open accordion: tapping the open book closes it; tapping any other
    // book opens it and closes whatever was open.
    setExpandedSlugs((prev) => (prev.has(slug) ? new Set() : new Set([slug])));
  };

  // Hold the tapped book's header at its pre-tap viewport position after a
  // single-open toggle collapses a book above it. No-op when nothing above the
  // tapped row changed (delta ≈ 0) and on mount/restore (the ref is null —
  // restore seeds expansion via the useState initializer, not toggleBook). The
  // body has no height transition, so layout is final here and the correction
  // lands before paint with no visible jump.
  useLayoutEffect(() => {
    const anchor = toggleAnchorRef.current;
    if (!anchor) return;
    toggleAnchorRef.current = null;
    const row = bookRowRefs.current.get(anchor.slug);
    if (!row) return;
    const delta = row.getBoundingClientRect().top - anchor.top;
    if (delta !== 0) window.scrollBy(0, delta);
  }, [expandedSlugs]);

  // Books-search expansion toggle (arc 07.2): also single-open, for one
  // consistent mental model with the main index. No scroll-anchor here — search
  // book lists are short and bounded, and this subtree re-renders on every
  // debounced keystroke, so scroll management would fight result churn.
  const toggleSearchBook = (slug: string) => {
    setSearchExpandedSlugs((prev) =>
      prev.has(slug) ? new Set() : new Set([slug]),
    );
  };

  const toggleTopic = (slug: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleStory = (id: string) => {
    setExpandedStories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const otBooks = useMemo(
    () => CANONICAL_BOOKS.filter((b) => testamentOf(b.order) === 'OT'),
    [],
  );
  const ntBooks = useMemo(
    () => CANONICAL_BOOKS.filter((b) => testamentOf(b.order) === 'NT'),
    [],
  );

  // Shared accordion-row renderer (arc 07.2). One renderer for both the main
  // index and the Books-search results, parametrized by expansion state +
  // toggle + chapter-open snapshot writer + an optional row ref (the index uses
  // it for browse-anchor restoration; search results do not). The expanded
  // body renders the static-count ChapterGrid UNCONDITIONALLY (2026-08-25):
  // the grid draws from CANONICAL_BOOKS with no per-book IDB read, and a
  // chapter tap navigates into the reader, which now self-serves the tapped
  // book via ensureBookAvailable (requested-book-first) — so grids no longer
  // wait on the index-level full-corpus hydration, and a failed hydration
  // surfaces only on the offline-status affordance below, never as a dead
  // book list.
  interface BookRowOpts {
    expanded: boolean;
    onToggle: () => void;
    onOpenChapter: (slug: string) => void;
    liRef?: (el: HTMLLIElement | null) => void;
  }
  const renderBookRow = (
    book: CanonicalBookEntry,
    opts: BookRowOpts,
  ): ReactNode => {
    const { expanded, onToggle, onOpenChapter, liRef } = opts;
    const bodyId = `bible-book-body-${book.slug}`;

    return (
      <li key={book.slug} className={styles.bookAccordionItem} ref={liRef}>
        <button
          type="button"
          className={styles.bookAccordionRow}
          aria-expanded={expanded}
          aria-controls={bodyId}
          aria-label={t(
            expanded ? 'bible.bookAriaCollapse' : 'bible.bookAriaExpand',
            { book: book.name },
          )}
          onClick={onToggle}
        >
          <span className={styles.bookAccordionName}>{book.bookTitle ?? book.name}</span>
          <ChevronToggle expanded={expanded} />
        </button>
        {expanded && (
          <div id={bodyId} className={styles.bookAccordionBody}>
            <ChapterGrid
              slug={book.slug}
              bookName={book.name}
              chapterCount={book.chapters}
              onOpenChapter={onOpenChapter}
              t={t}
            />
          </div>
        )}
      </li>
    );
  };

  // Main-index rows: browse expansion, browse snapshot, browse-anchor ref.
  const renderBook = (book: CanonicalBookEntry) =>
    renderBookRow(book, {
      expanded: expandedSlugs.has(book.slug),
      onToggle: () => toggleBook(book.slug),
      onOpenChapter: writeBrowseSnapshot,
      liRef: (el) => {
        if (el) bookRowRefs.current.set(book.slug, el);
        else bookRowRefs.current.delete(book.slug);
      },
    });

  // Book results (arc 07.2): synchronous over CANONICAL_BOOKS, so derived
  // directly from the debounced query (no async stamp / settle to wait on).
  const liveBooks = useMemo(() => {
    const q = debouncedQuery.trim();
    return q ? searchBooks(q, BOOK_RESULT_LIMIT) : [];
  }, [debouncedQuery]);
  const hasBooks = liveBooks.length > 0;

  // Only show a result whose stamp matches the live debounced query.
  const liveResult =
    verseSearch && verseSearch.query === debouncedQuery.trim()
      ? verseSearch.result
      : null;

  // Topic results gated the same way: only a stamp matching the live
  // debounced query renders. liveTopicEntries carries the 08.4 strength bands;
  // liveTopics maps back to BundledTopic[] so every existing topic renderer /
  // count / announce is untouched.
  const liveTopicEntries =
    topicSearch && topicSearch.query === debouncedQuery.trim()
      ? topicSearch.topics
      : null;
  const liveTopics =
    liveTopicEntries != null ? liveTopicEntries.map((x) => x.topic) : null;
  const hasTopics = liveTopics != null && liveTopics.length > 0;
  // Strongest band among the live topic matches (08.4), for orderTopSections;
  // null when no topic matched. ('none' only appears for the empty-query
  // passthrough, which never reaches active-search ordering.)
  const topicBestStrength: TopicMatchStrength | null =
    liveTopicEntries && liveTopicEntries.length > 0
      ? liveTopicEntries.reduce<TopicMatchStrength>(
          (best, e) =>
            TOPIC_STRENGTH_RANK[e.strength] < TOPIC_STRENGTH_RANK[best]
              ? e.strength
              : best,
          liveTopicEntries[0].strength,
        )
      : null;

  // Story results gated the same way: only a stamp matching the live
  // debounced query renders.
  const liveStories =
    storySearch && storySearch.query === debouncedQuery.trim()
      ? storySearch.stories
      : null;
  const hasStories = liveStories != null && liveStories.length > 0;

  // Verse, topic, and story searches settle independently (liveResult /
  // liveTopics / liveStories flip from null to a value as each lands). Books
  // are synchronous (hasBooks is always settled for the live query). Gate BOTH
  // the "Searching…" line and the "No verses found" line on the async paths
  // having settled, so a fast empty verse result never flashes "No verses
  // found" before matching topics or stories arrive. Something is displayable
  // once a reference jump, matched books, matched topics, matched stories, or
  // verse hits exist.
  const hasVisibleResults =
    parsedRef != null ||
    hasBooks ||
    hasTopics ||
    hasStories ||
    (liveResult != null && liveResult.hits.length > 0);
  const resultsPending =
    isSearching &&
    !hasVisibleResults &&
    (liveResult === null || liveTopics === null || liveStories === null);
  const noMatch =
    isSearching &&
    !parsedRef &&
    !hasBooks &&
    !hasTopics &&
    !hasStories &&
    liveResult != null &&
    liveResult.hits.length === 0 &&
    liveTopics !== null &&
    liveStories !== null;

  // Single computed string for the persistent aria-live region below, so
  // screen readers announce searching / topic + verse counts / no-match as
  // the search state changes. The visible status lines mirror this text but
  // are aria-hidden to avoid a double read. A persistent (always-mounted)
  // region is used so the '' → "Searching…" transition is a mutation the
  // SR reliably announces (a region that mounts already populated may not).
  const announceParts: string[] = [];
  if (hasBooks) {
    announceParts.push(t('bibleSearch.bookCount', { count: liveBooks.length }));
  }
  if (hasTopics && liveTopics) {
    announceParts.push(t('bibleSearch.topicCount', { count: liveTopics.length }));
  }
  if (hasStories && liveStories) {
    announceParts.push(t('bibleSearch.storyCount', { count: liveStories.length }));
  }
  if (liveResult && liveResult.hits.length > 0) {
    if (liveResult.fuzzy) announceParts.push(t('bibleSearch.fuzzyNote'));
    if (liveResult.topMatchKind === 'related' && liveResult.relatedTerms.length > 0) {
      announceParts.push(
        t('bibleSearch.relatedNote', {
          terms: liveResult.relatedTerms.map((rt) => rt.to).join(', '),
        }),
      );
    }
    announceParts.push(t('bibleSearch.count', { count: liveResult.hits.length }));
  }
  const searchAnnouncement = !isSearching
    ? ''
    : resultsPending
      ? t('bibleSearch.searching')
      : announceParts.length > 0
        ? announceParts.join('. ')
        : noMatch
          ? t('bibleSearch.noMatch')
          : '';

  // --- Result-type tabs (arc 07.3) ----------------------------------------
  // Present categories in CHIP order (verses, books, topics, stories). The
  // reference banner is pinned above the chips, not a category. 'Top' chip
  // appears only with >= 2 present categories; the chip row only renders with
  // >= 2 chips. effectiveTab is derived every render so an async settle that
  // empties the active category falls back cleanly.
  const hasVerses = liveResult != null && liveResult.hits.length > 0;
  const presentCats: Exclude<SearchTab, 'top'>[] = [];
  if (hasVerses) presentCats.push('verses');
  if (hasBooks) presentCats.push('books');
  if (hasTopics) presentCats.push('topics');
  if (hasStories) presentCats.push('stories');
  const showTop = presentCats.length >= 2;
  const chipTabs: SearchTab[] = showTop ? ['top', ...presentCats] : presentCats;
  const effectiveTab: SearchTab = chipTabs.includes(activeTab)
    ? activeTab
    : showTop
      ? 'top'
      : (presentCats[0] ?? 'top');
  const showChips = chipTabs.length >= 2;

  // "See all (N)" affordance in the Top preview: selecting it switches to that
  // category's chip (full list).
  const seeAllButton = (tab: Exclude<SearchTab, 'top'>, total: number): ReactNode => (
    <button
      type="button"
      className={styles.seeAllBtn}
      onClick={() => setActiveTab(tab)}
    >
      {t('bibleSearch.seeAll', { count: total })}
    </button>
  );

  // Each category renders via a helper taking a cap (null = full list, a number
  // = Top-preview slice with a "See all" when it overflows). The full-list view
  // (cap null) keeps the original count line / refine hint; the preview adds a
  // heading + See-all instead.
  const renderBooksSection = (cap: number | null): ReactNode => {
    const shown = cap == null ? liveBooks : liveBooks.slice(0, cap);
    const overflow = cap != null && liveBooks.length > cap;
    return (
      <section
        className={styles.bookResultSection}
        aria-label={t('bibleSearch.booksHeading')}
      >
        <p className={styles.bookResultHeading} aria-hidden="true">
          {t('bibleSearch.booksHeading')}
        </p>
        <ul className={styles.bookAccordionList}>
          {shown.map((book) =>
            renderBookRow(book, {
              expanded: searchExpandedSlugs.has(book.slug),
              onToggle: () => toggleSearchBook(book.slug),
              // Books-search chapter opens write a SEARCH snapshot so Back
              // returns to the search with the query intact (not a browse
              // snapshot), consistent with verse/topic/story result links.
              onOpenChapter: () => writeSearchSnapshot(debouncedQuery.trim()),
            }),
          )}
        </ul>
        {overflow && seeAllButton('books', liveBooks.length)}
      </section>
    );
  };

  const renderTopicsSection = (cap: number | null): ReactNode => {
    if (!liveTopics) return null;
    const shown = cap == null ? liveTopics : liveTopics.slice(0, cap);
    const overflow = cap != null && liveTopics.length > cap;
    return (
      <section
        className={styles.topicSection}
        aria-label={t('bibleSearch.topicsHeading')}
      >
        <p className={styles.topicHeading} aria-hidden="true">
          {t('bibleSearch.topicsHeading')}
        </p>
        <ul className={styles.topicList}>
          {shown.map((topic) => {
            const expanded = expandedTopics.has(topic.slug);
            const shownVerses = expanded
              ? topic.verses
              : topic.verses.slice(0, TOPIC_VERSE_PREVIEW);
            const hasMore = topic.verses.length > TOPIC_VERSE_PREVIEW;
            return (
              <li key={topic.slug} className={styles.topicItem}>
                <p className={styles.topicName}>{topic.name}</p>
                <ul className={styles.topicVerseList}>
                  {shownVerses.map((verse) => (
                    <li
                      key={`${verse.bookSlug}-${verse.chapter}-${verse.verseStart}-${verse.verseEnd}`}
                      className={styles.topicVerseItem}
                    >
                      <TopicVerseRow
                        verse={verse}
                        onNavigate={() => writeSearchSnapshot(debouncedQuery.trim())}
                        t={t}
                      />
                    </li>
                  ))}
                </ul>
                {hasMore && (
                  <button
                    type="button"
                    className={styles.topicMore}
                    aria-expanded={expanded}
                    onClick={() => toggleTopic(topic.slug)}
                  >
                    {expanded
                      ? t('bibleSearch.topicShowFewer')
                      : t('bibleSearch.topicShowAll', { count: topic.verses.length })}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {overflow && seeAllButton('topics', liveTopics.length)}
      </section>
    );
  };

  const renderStoriesSection = (cap: number | null): ReactNode => {
    if (!liveStories) return null;
    const shown = cap == null ? liveStories : liveStories.slice(0, cap);
    const overflow = cap != null && liveStories.length > cap;
    return (
      <section
        className={styles.storySection}
        aria-label={t('bibleSearch.storiesHeading')}
      >
        <p className={styles.storyHeading} aria-hidden="true">
          {t('bibleSearch.storiesHeading')}
        </p>
        <ul className={styles.storyList}>
          {shown.map((story) => {
            const id = storyId(story);
            return (
              <li key={id} className={styles.storyItem}>
                <StoryCard
                  story={story}
                  expanded={expandedStories.has(id)}
                  onToggle={() => toggleStory(id)}
                  onNavigate={() => writeSearchSnapshot(debouncedQuery.trim())}
                  t={t}
                />
              </li>
            );
          })}
        </ul>
        {overflow && seeAllButton('stories', liveStories.length)}
      </section>
    );
  };

  const renderVersesSection = (cap: number | null): ReactNode => {
    if (!liveResult || liveResult.hits.length === 0) return null;
    const shown = cap == null ? liveResult.hits : liveResult.hits.slice(0, cap);
    const overflow = cap != null && liveResult.hits.length > cap;
    return (
      <section
        className={styles.verseResultSection}
        aria-label={t('bibleSearch.tabVerses')}
      >
        {/* Heading only in the Top preview; the full Verses tab is self-evident
            and keeps its count line instead. */}
        {cap != null && (
          <p className={styles.verseResultHeading} aria-hidden="true">
            {t('bibleSearch.tabVerses')}
          </p>
        )}
        {liveResult.fuzzy && (
          <p className={styles.fuzzyNote} aria-hidden="true">
            {t('bibleSearch.fuzzyNote')}
          </p>
        )}
        {liveResult.topMatchKind === 'related' && liveResult.relatedTerms.length > 0 && (
          <p className={styles.fuzzyNote} aria-hidden="true">
            {t('bibleSearch.relatedNote', {
              terms: liveResult.relatedTerms.map((rt) => rt.to).join(', '),
            })}
          </p>
        )}
        {cap == null && (
          <p className={styles.resultCount} aria-hidden="true">
            {t('bibleSearch.count', { count: liveResult.hits.length })}
          </p>
        )}
        <ul className={styles.resultList}>
          {shown.map((hit) => {
            const label = `${
              CANONICAL_SLUG_TO_NAME.get(hit.bookSlug) ?? hit.bookName
            } ${hit.chapter}:${hit.verse}`;
            return (
              <li
                key={`${hit.bookSlug}-${hit.chapter}-${hit.verse}`}
                className={styles.resultItem}
              >
                <Link
                  to={`/bible/${hit.bookSlug}/${hit.chapter}?v=${hit.verse}`}
                  className={styles.resultLink}
                  onClick={() => writeSearchSnapshot(debouncedQuery.trim())}
                  aria-label={t('bibleSearch.resultAria', { reference: label })}
                >
                  <span className={styles.resultRef}>{label}</span>
                  <span className={styles.resultSnippet}>
                    {highlightSnippet(hit.snippet, hit.spans, styles.snippetMark)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        {cap == null && liveResult.capped && (
          <p className={styles.refineHint}>
            {t('bibleSearch.refineHint', { count: liveResult.hits.length })}
          </p>
        )}
        {overflow && seeAllButton('verses', liveResult.hits.length)}
      </section>
    );
  };

  return (
    <div
      className={styles.container}
      data-entry-settling={entrySettled ? undefined : ''}
      style={
        noticeClearance > 0
          ? ({ '--notice-clearance': `${noticeClearance}px` } as CSSProperties)
          : undefined
      }
    >
      <Link to="/" className={styles.backLink}>
        {t('bible.back')}
      </Link>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <h1 className={styles.title}>{t('bible.title')}</h1>

      {/* Offline status (offline-first Chunk 3 §4.2/§4.3): a visible
          "preparing..." moment while the Bible downloads, an "available
          offline" reassurance once it has, and an explicit download/retry
          affordance otherwise. No move to boot (rule #66) -- this only makes
          the already-visit-triggered hydration visible. */}
      {!reading.restricted && !definitelyOffline && <div className={styles.offlineStatus}>
        <BibleBootstrapIndicator />
        {isBibleBootstrapping && bootstrapBooksTotal > 0 && (
          <p className={styles.offlineStatusLine} role="status">
            {t('bible.savingProgress', {
              done: bootstrapBooksDone,
              total: bootstrapBooksTotal,
            })}
          </p>
        )}
        {!isBibleBootstrapping && hasMeta === true && (
          <p className={styles.offlineStatusLine} role="status">
            {t('bible.offlineAvailable')}
          </p>
        )}
        {!isBibleBootstrapping &&
          hasMeta === false &&
          hydrationState !== 'pending' &&
          // Every branch is a REAL button wired to startHydration
          // (2026-08-25 — the offline line used to be a non-interactive
          // paragraph whose copy said "Tap to retry"). Failed-offline
          // gets the connect-once copy; failed-online gets the honest
          // download-failed copy; the residual not-failed edge keeps the
          // plain download affordance.
          (hydrationState === 'failed' ? (
            <button
              type="button"
              className={styles.offlineDownloadBtn}
              onClick={startHydration}
            >
              {t(
                hydrationOffline
                  ? 'bible.offlineNeedsDownload'
                  : 'bible.downloadFailedRetry',
              )}
            </button>
          ) : (
            <button
              type="button"
              className={styles.offlineDownloadBtn}
              onClick={startHydration}
            >
              {t('bible.downloadForOffline')}
            </button>
          ))}
      </div>}

      <div className={styles.searchRow}>
        <div className={styles.searchWrap}>
          <svg
            className={styles.searchIcon}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            className={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={(e) => {
              // One-shot select-all after a search restore so one keystroke
              // replaces the restored query (navigation chunk C, decision #1).
              if (pendingSelectAllRef.current) {
                e.currentTarget.select();
                pendingSelectAllRef.current = false;
              }
            }}
            placeholder={t('bibleSearch.placeholder')}
            aria-label={t('bibleSearch.placeholder')}
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => setQuery('')}
              aria-label={t('bibleSearch.clear')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className={styles.srOnly} role="status" aria-live="polite">
        {searchAnnouncement}
      </div>

      {isSearching ? (
        <div className={styles.searchResults}>
          {parsedRef && (
            <Link
              to={referenceTo(parsedRef)}
              className={styles.goToReference}
              onClick={() => writeSearchSnapshot(trimmedQuery)}
            >
              <svg
                className={styles.goToReferenceIcon}
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
              <span>
                {t('bibleSearch.goToReference', {
                  reference: referenceLabel(parsedRef),
                })}
              </span>
            </Link>
          )}

          {showChips && (
            <div
              className={styles.searchTabs}
              role="group"
              aria-label={t('bibleSearch.tabsAria')}
            >
              {chipTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={styles.searchTab}
                  aria-pressed={effectiveTab === tab}
                  onClick={() => setActiveTab(tab)}
                >
                  {t(TAB_LABEL_KEY[tab])}
                </button>
              ))}
            </div>
          )}

          {effectiveTab === 'top' ? (
            <>
              {/* Top grouped preview: section order is intent-ranked (08.4) via
                  orderTopSections — Books first, Stories last, Verses↔Topics
                  swapped by match strength. It emits only present sections, so
                  the per-section has* gate is folded into the ordering; the
                  renderers, caps, and per-tab views are unchanged. */}
              {orderTopSections({
                hasBooks,
                hasVerses,
                hasTopics,
                hasStories,
                verseTopKind: liveResult?.topMatchKind ?? null,
                topicTopStrength: topicBestStrength,
              }).map((section: TopSection) => {
                switch (section) {
                  case 'books':
                    return (
                      <Fragment key="books">{renderBooksSection(TOP_BOOK_CAP)}</Fragment>
                    );
                  case 'verses':
                    return (
                      <Fragment key="verses">{renderVersesSection(TOP_VERSE_CAP)}</Fragment>
                    );
                  case 'topics':
                    return (
                      <Fragment key="topics">{renderTopicsSection(TOP_TOPIC_CAP)}</Fragment>
                    );
                  case 'stories':
                    return (
                      <Fragment key="stories">{renderStoriesSection(TOP_STORY_CAP)}</Fragment>
                    );
                  default:
                    return null;
                }
              })}
            </>
          ) : effectiveTab === 'books' ? (
            renderBooksSection(null)
          ) : effectiveTab === 'verses' ? (
            renderVersesSection(null)
          ) : effectiveTab === 'topics' ? (
            renderTopicsSection(null)
          ) : effectiveTab === 'stories' ? (
            renderStoriesSection(null)
          ) : null}

          {resultsPending && (
            <p className={styles.searchStatus} aria-hidden="true">
              {t('bibleSearch.searching')}
            </p>
          )}

          {noMatch && (
            <p className={styles.searchStatus} aria-hidden="true">
              {t('bibleSearch.noMatch')}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Entry zone (index entry redesign, 2026-10-10): the resume hero
              first, then the two doorway tiles. Both are a different SHAPE from
              the book rows below, so they never read as more rows in the list. */}
          <ContinueReadingCard onNavigate={writeNeutralSnapshot} onSettled={markEntrySettled} />
          {((!reading.restricted && SCRIPTURE_JOURNAL_ENABLED) || showOverviewLink) && (
            <div className={styles.entryTiles}>
              {!reading.restricted && SCRIPTURE_JOURNAL_ENABLED && (
                <Link to="/bible/bookmarks" className={styles.entryTile}>
                  <svg
                    className={styles.entryTileIcon}
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
                    <line x1="16" y1="8" x2="2" y2="22" />
                    <line x1="17.5" y1="15" x2="9" y2="15" />
                  </svg>
                  <span className={styles.entryTileTitle}>
                    {t('bible.journalButton')}
                  </span>
                  {journalCount > 0 && (
                    <span className={styles.entryTileMeta}>
                      {t('bible.journalButtonCount', { count: journalCount })}
                    </span>
                  )}
                  {journalBand.length > 0 && (
                    <span className={styles.entryTileBand} aria-hidden="true">
                      {journalBand.map(({ family, count }) => (
                        <span
                          key={family}
                          className={styles.entryTileBandSegment}
                          style={{ flexGrow: count, backgroundColor: HL_DOT_HEX[family] }}
                        />
                      ))}
                    </span>
                  )}
                </Link>
              )}
              {showOverviewLink && (
                <Link to="/bible/overview" className={styles.entryTile}>
                  <svg
                    className={styles.entryTileIcon}
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                  </svg>
                  <span className={styles.entryTileTitle}>
                    {t('bibleOverview.indexRowTitle')}
                  </span>
                  <span className={styles.entryTileMeta}>
                    {t('bibleOverview.indexRowHelper')}
                  </span>
                </Link>
              )}
            </div>
          )}

          <section className={styles.testamentSection}>
            <h2 className={styles.sectionHeading}>{t('bible.oldTestament')}</h2>
            <ul className={styles.bookAccordionList}>
              {otBooks.map(renderBook)}
            </ul>
          </section>

          <section className={styles.testamentSection}>
            <h2 className={styles.sectionHeading}>{t('bible.newTestament')}</h2>
            <ul className={styles.bookAccordionList}>
              {ntBooks.map(renderBook)}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

interface ChapterGridProps {
  slug: string;
  bookName: string;
  chapterCount: number;
  // Browse-intent snapshot writer (navigation chunk C); runs before the cell
  // Link navigates so the index restores this book's expansion + anchor on back.
  onOpenChapter: (slug: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function ChapterGrid({
  slug,
  bookName,
  chapterCount,
  onOpenChapter,
  t,
}: ChapterGridProps) {
  const numbers = useMemo(
    () => Array.from({ length: chapterCount }, (_, i) => i + 1),
    [chapterCount],
  );
  return (
    <ul className={styles.chapterGrid}>
      {numbers.map((n) => (
        <li key={n} className={styles.chapterCellItem}>
          <Link
            to={`/bible/${slug}/${n}`}
            className={styles.chapterCell}
            onClick={() => onOpenChapter(slug)}
            aria-label={t('bible.chapterAriaLabel', {
              book: bookName,
              chapter: n,
            })}
          >
            {n}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ChevronToggle({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={styles.chevron}
      data-expanded={expanded || undefined}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Cached search works with any downloaded books, including a partial corpus.
// Only a verified online reader may improve coverage by hydrating missing text.
function prepareCachedSearch(): Promise<void> {
  return getReadingAccessSnapshot().restricted || navigator.onLine === false
    ? Promise.resolve() : ensureBibleHydrated();
}

interface TopicVerseRowProps {
  verse: BundledTopicVerse;
  // Search-intent snapshot writer (navigation chunk C); runs before the verse
  // Link navigates so the index restores the query on back.
  onNavigate: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

// A curated topic verse. The bundle carries refs only, so the BSB text is
// resolved at render (BSB single source, rule #62/#76): the reference shows
// immediately and the text fills in once resolved. The whole row is a
// router Link to the chapter, verse-targeted via ?v= (the BibleChapter
// scroll-to-verse branch, the same mechanism the verse hits use).
function TopicVerseRow({ verse, onNavigate, t }: TopicVerseRowProps) {
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    prepareCachedSearch()
      .then(() => resolveTopicVerseText(verse))
      .then((resolved) => {
        if (!cancelled) setText(resolved);
      })
      .catch(() => {
        if (!cancelled) setText('');
      });
    return () => {
      cancelled = true;
    };
  }, [verse]);

  return (
    <Link
      to={`/bible/${verse.bookSlug}/${verse.chapter}?v=${verse.verseStart}`}
      className={styles.topicVerseLink}
      onClick={onNavigate}
      aria-label={t('bibleSearch.resultAria', { reference: verse.referenceText })}
    >
      <span className={styles.topicVerseRef}>{verse.referenceText}</span>
      {text ? <span className={styles.topicVerseText}>{text}</span> : null}
    </Link>
  );
}

interface StoryCardProps {
  story: BundledStory;
  expanded: boolean;
  onToggle: () => void;
  // Search-intent snapshot writer (navigation chunk C); runs before the pivot
  // Link navigates so the index restores the query on back.
  onNavigate: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

// A spine-catalog story (rule #76 Layer-2 prose card): heading + procession
// line + clamped prose (tap to expand) + a gold pivot-verse pill. The bundle
// carries the pivot REF string only (rule #76 single source); parseReference
// (the facade parser) drives both the canonical label and the ?v= jump
// target, and the pivot verse TEXT resolves from bundled BSB at render via
// the same resolveTopicVerseText the topic verses use (decision 3). Tapping
// the pill navigates to the chapter at the pivot verse via the chunk-03
// scroll-to-verse mechanism. A pivotRef that fails to parse (defensive; the
// build + probe guarantee all bundled rows parse) simply hides the pill.
function StoryCard({ story, expanded, onToggle, onNavigate, t }: StoryCardProps) {
  const pivot = useMemo(() => parseReference(story.pivotRef), [story.pivotRef]);
  const [pivotText, setPivotText] = useState<string | null>(null);

  useEffect(() => {
    // No pivot to resolve: pivotText stays at its initial null (the pill is
    // not rendered in this case anyway). The component is keyed by storyId,
    // so it remounts per story and there is no stale value to clear here,
    // avoiding a synchronous setState in the effect body (rule #75).
    if (!pivot || pivot.verseStart == null) return;
    const { bookSlug, chapter, verseStart, verseEnd } = pivot;
    let cancelled = false;
    prepareCachedSearch()
      .then(() =>
        resolveTopicVerseText({
          bookSlug,
          chapter,
          verseStart,
          verseEnd: verseEnd ?? verseStart,
        }),
      )
      .then((resolved) => {
        if (!cancelled) setPivotText(resolved);
      })
      .catch(() => {
        if (!cancelled) setPivotText('');
      });
    return () => {
      cancelled = true;
    };
  }, [pivot]);

  const heading = story.storyName ?? story.processionName;

  return (
    <div className={styles.storyCard}>
      <p className={styles.storyName}>{heading}</p>
      <p className={styles.storyProcession}>
        {`${story.processionName}, ${story.roleDescriptor}.`}
      </p>
      <p className={styles.storyProse} data-expanded={expanded || undefined}>
        {story.displayProse}
      </p>
      <button
        type="button"
        className={styles.storyMore}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        {expanded
          ? t('bibleSearch.storyReadLess')
          : t('bibleSearch.storyReadMore')}
      </button>
      {pivot && pivot.verseStart != null && (
        <Link
          to={referenceTo(pivot)}
          className={styles.storyPivotLink}
          onClick={onNavigate}
          aria-label={t('bibleSearch.resultAria', {
            reference: referenceLabel(pivot),
          })}
        >
          <span className={styles.storyPivotRef}>{`✦ ${referenceLabel(pivot)}`}</span>
          {pivotText ? (
            <span className={styles.storyPivotText}>{pivotText}</span>
          ) : null}
        </Link>
      )}
    </div>
  );
}
